import React, { useEffect, useRef } from 'react';
import { boundsOf } from '../lib/geo';

/* Canvas-based renderer used when offline or as a fallback.
   Draws breadcrumb polyline + start/end markers + current position on a dark grid. */
export default function CanvasMap({ points = [], current = null, waypoints = [], height = '100%' }) {
  const ref = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const wrap = wrapRef.current;
    const W = wrap.clientWidth;
    const H = wrap.clientHeight;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width = `${W}px`;
    canvas.style.height = `${H}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // Background + grid
    ctx.fillStyle = '#0A0A0A';
    ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = 'rgba(255,255,255,0.05)';
    ctx.lineWidth = 1;
    const step = 32;
    for (let x = 0; x < W; x += step) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
    for (let y = 0; y < H; y += step) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }

    const all = current ? [...points, current, ...waypoints] : [...points, ...waypoints];
    if (all.length === 0) {
      ctx.fillStyle = '#71717A';
      ctx.font = '12px Manrope, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Waiting for GPS…', W / 2, H / 2);
      return;
    }

    const b = boundsOf(all);
    const padding = 30;
    const spanLat = Math.max(1e-6, b.maxLat - b.minLat);
    const spanLng = Math.max(1e-6, b.maxLng - b.minLng);
    // preserve aspect
    const scale = Math.min((W - 2 * padding) / spanLng, (H - 2 * padding) / spanLat);
    const cx = W / 2;
    const cy = H / 2;
    const midLat = (b.maxLat + b.minLat) / 2;
    const midLng = (b.maxLng + b.minLng) / 2;
    const project = (p) => ({
      x: cx + (p.lng - midLng) * scale,
      y: cy - (p.lat - midLat) * scale,
    });

    // Route polyline
    if (points.length > 1) {
      ctx.strokeStyle = '#FF9F1C';
      ctx.lineWidth = 3;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.beginPath();
      points.forEach((p, i) => {
        const q = project(p);
        if (i === 0) ctx.moveTo(q.x, q.y);
        else ctx.lineTo(q.x, q.y);
      });
      ctx.stroke();
    }

    // Breadcrumb dots
    ctx.fillStyle = 'rgba(255,159,28,0.4)';
    points.forEach((p) => {
      const q = project(p);
      ctx.beginPath();
      ctx.arc(q.x, q.y, 1.5, 0, Math.PI * 2);
      ctx.fill();
    });

    // Start marker
    if (points[0]) {
      const s = project(points[0]);
      ctx.fillStyle = '#06D6A0';
      ctx.beginPath();
      ctx.arc(s.x, s.y, 7, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#0A0A0A';
      ctx.lineWidth = 3;
      ctx.stroke();
      ctx.fillStyle = '#06D6A0';
      ctx.font = 'bold 10px Chivo, sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText('START', s.x + 10, s.y + 4);
    }
    // End marker
    if (points.length > 1) {
      const e = project(points[points.length - 1]);
      ctx.fillStyle = '#FF9F1C';
      ctx.beginPath();
      ctx.arc(e.x, e.y, 7, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#0A0A0A';
      ctx.lineWidth = 3;
      ctx.stroke();
    }
    // Current position
    if (current) {
      const c = project(current);
      ctx.fillStyle = '#118AB2';
      ctx.beginPath();
      ctx.arc(c.x, c.y, 9, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.fillStyle = '#118AB2';
      ctx.beginPath();
      ctx.arc(c.x, c.y, 16, 0, Math.PI * 2);
      ctx.globalAlpha = 0.25;
      ctx.fill();
      ctx.globalAlpha = 1;
    }

    // Waypoints
    waypoints.forEach((w) => {
      const q = project(w);
      const color = w.color || '#FFD166';
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(q.x, q.y, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#0A0A0A';
      ctx.lineWidth = 2;
      ctx.stroke();
      if (w.name) {
        ctx.fillStyle = color;
        ctx.font = 'bold 9px Chivo, sans-serif';
        ctx.textAlign = 'left';
        ctx.fillText(w.name, q.x + 8, q.y + 3);
      }
    });
  }, [points, current, waypoints]);

  return (
    <div ref={wrapRef} className="w-full h-full rounded-2xl overflow-hidden bg-[#0A0A0A] border border-white/5" style={{ height }}>
      <canvas ref={ref} data-testid="canvas-map" />
    </div>
  );
}
